import React, { ChangeEvent, useEffect, useState } from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";
import Select, { components } from "react-select";
import { getIsDarkMode } from "@/store/slices/webConfig";
import styles from "./select.module.css";

type FieldVariant = "default" | "underline";
export interface SelectV2Props {
  options: { value: any; label: string }[];
  isDarkMode?: boolean;
  defaultValue?: any;
  className?: string;
  menuClassName?: string;
  additionalWrapperClassNames?: string;
  iconComponent?: React.ReactNode;
  label?: string;
  name?: string;
  value?: any;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  id?: string;
  endAdornment?: React.ReactNode;
  additionalInputClassNames?: string;
  placeholder?: string;
  required?: boolean;
  tooltipText?: string;
  customStyles?: object;
  viewHeight?: string;
  ellipsis?: boolean;
  variant?: FieldVariant;
  preSelectedValue?: any;
  showValueOnSelect?: boolean;
}

function SelectV2({
  options,
  className,
  tooltipText,
  iconComponent,
  label,
  name,
  value,
  customStyles,
  required,
  onChange,
  id,
  endAdornment,
  additionalInputClassNames,
  additionalWrapperClassNames,
  placeholder,
  defaultValue,
  preSelectedValue,
  ellipsis,
  viewHeight = "h-12",
  variant = "default",
  showValueOnSelect = false,
  ...rest
}: SelectV2Props) {
  const { DropdownIndicator, IndicatorSeparator } = components;
  const [selectedValue, setSelectedValue] = useState<any>(null);
  const isDarkMode = useSelector(getIsDarkMode);

  const CustomSingleValue = (props: any) => {
    const { data } = props;
    return (
      <components.SingleValue {...props}>
        {showValueOnSelect ? data.value : data.label}
      </components.SingleValue>
    );
  };

  const wrapperDynamicClassNames = clsx(additionalWrapperClassNames, {
    "flex flex-col relative": true
  });

  const inputDynamicClassNames = clsx(`${additionalInputClassNames} ${viewHeight}`, {
    "w-full focus:outline-none pl-[1px] dark:text-white": true,
    "border-gray-400 border-[1px] focus-within:border-[2px] focus:border-primary rounded-md rounded-tr-none  bg-white placeholder-gray-600 focus:border-primary focus-within:border-primary dark:bg-gray-900 dark:hover:border-white dark:focus-within:border-white  hover:border-gray-600":
      variant === "default",
    "border-b-2 border-primary pt-2": variant === "underline",
    "pl-2": placeholder || value
  });

  const handleInputChange = (selectedOption: any) => {
    setSelectedValue(selectedOption);
    if (onChange) {
      if (selectedOption?.value === true) return;
      onChange(selectedOption);
    }
  };

  useEffect(() => setSelectedValue(options?.find((option) => option.value === defaultValue)), [options, defaultValue]);

  useEffect(() => {
    if (preSelectedValue !== undefined) {
      const defaultOption = options.find(option => option.label === preSelectedValue);
      setSelectedValue(defaultOption || null);
    }
  }, [options, preSelectedValue]);

  customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      border: "none",
      boxShadow: state.isFocused || state.isHovered ? "none" : null,
      height: "100%",
      width: `${ellipsis ? "90%" : "100%"}`,
      backgroundColor: `${isDarkMode ? "var(--color-gray-900)" : "var(--color-white)"}`,
      color: `${isDarkMode ? "var(--color-white)" : "var(--color-gray-900)"}`
    }),
    singleValue: (provided: any) => ({
      ...provided,
      display: `${ellipsis ? "block" : "flex"}`,
      alignItems: "left",
      color: `${isDarkMode ? "var(--color-white)" : "var(--color-gray-950)"}`,
      marginTop: `${showValueOnSelect && "4px"}`,
      fontWeight: `${showValueOnSelect && "600"}`,
      marginLeft: `${showValueOnSelect && "0px"}`
    }),

    option: (provided: any, state: any) => ({
      ...provided,
      display: "block",
      backgroundColor: state.isSelected
        ? `${isDarkMode ? "var(--color-gray-400)" : "var(--color-orange-100)"}`
        : state.isFocused
        ? `${isDarkMode ? "var(--color-gray-200)" : "var(--color-gray-150)"}`
        : null,
      alignItems: "left",
      justifyContent: "left",
      color: `${isDarkMode ? "var(--color-white)" : "var(--color-gray-900)"}`,
      className: "custom-options-class"
    }),

    menu: (provided: any) => ({
      ...provided,
      color: `${isDarkMode ? "var(--color-gray-950)" : "var(--color-white)"}`,
      backgroundColor: `${isDarkMode ? "var(--color-gray-950)" : "var(--color-white)"}`,
      borderRadius: "10px",
      minWidth: "200px"
    }),
    menuList: (provided: any) => ({
      ...provided,
      backgroundColor: `${isDarkMode ? "var(--color-gray-800)" : null}`,
      outerWidth: "20px",
      innerWidth: "20px"
    }),

    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 9999
    })
  };

  return (
    <div className={clsx(wrapperDynamicClassNames, "relative")}>
      <div className="group flex flex-col item-center">
        <Select
          options={options}
          aria-label={label}
          menuPosition="absolute"
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          className={clsx(inputDynamicClassNames, styles.select)}
          placeholder={
            required ? (
              <span>
                {placeholder}&nbsp;<span className="text-red-500">*</span>
              </span>
            ) : (
              placeholder
            )
          }
          styles={customStyles}
          components={{
            DropdownIndicator: iconComponent ? () => <div>{iconComponent}</div> : DropdownIndicator,
            IndicatorSeparator,
            IndicatorsContainer: () => <div></div>,
            SingleValue: CustomSingleValue
          }}
          aria-labelledby={label}
          name={name}
          inputId={id}
          required={required}
          value={selectedValue}
          onChange={handleInputChange}
          formatOptionLabel={(option: any) => option.label}
          isSearchable={false}
          {...rest}
        />
        {placeholder && (
          <label
            htmlFor={placeholder}
            className={clsx(
              "absolute top-[-6px] text-md flex items-center text-[#666666] dark:text-white dark:!bg-gray-900 hover:cursor-text transition-all bg-white px-1 text-xs -translate-x-[4px] dark:bg-gray-900 z-8",
              { hidden: !selectedValue },
              {
                "left-3": variant === "default",
                "left-0": variant === "underline"
              },
            )}
          >
            {placeholder}
            {required && <span className="text-red-500">&nbsp;*</span>}
          </label>
        )}
        {endAdornment && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center justify-center pointer-events-none">
            {endAdornment}
          </div>
        )}
      </div>
    </div>
  );
}

export default SelectV2;
